<!-- 商家管理中心 - 经营概况 Dashboard -->
<template>
  <div class="dashboard-container">
    <!-- 欢迎区域 -->
    <ContentWrap class="mb-16px">
      <div class="welcome-section">
        <div class="welcome-left">
          <h2 class="welcome-title">
            {{ greeting }}，{{ shopInfo?.shopName || '商家' }}
          </h2>
          <p class="welcome-subtitle">欢迎回到商家管理中心，以下是您的经营概况</p>
        </div>
        <div class="welcome-right">
          <el-tag v-if="shopInfo?.shopStatus === 1" type="success" size="large">正常营业</el-tag>
          <el-tag v-else-if="shopInfo?.shopStatus === 2" type="danger" size="large">已冻结</el-tag>
          <el-tag v-else-if="shopInfo?.shopStatus === 3" type="warning" size="large">关店中</el-tag>
          <el-tag v-else-if="shopInfo?.shopStatus === 4" type="info" size="large">已关闭</el-tag>
          <el-tag v-else type="info" size="large">未激活</el-tag>
        </div>
      </div>
    </ContentWrap>

    <!-- 数据概览卡片 -->
    <el-row :gutter="16" class="mb-16px">
      <!-- 今日订单 -->
      <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-content">
            <div class="stat-icon order-icon">
              <Icon icon="ep:document" :size="28" />
            </div>
            <div class="stat-info">
              <div class="stat-value">
                <el-skeleton v-if="loading" :rows="1" animated />
                <span v-else>{{ todayOrders }}</span>
              </div>
              <div class="stat-label">今日订单</div>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- 待发货 -->
      <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-content">
            <div class="stat-icon ship-icon">
              <Icon icon="ep:van" :size="28" />
            </div>
            <div class="stat-info">
              <div class="stat-value">
                <el-skeleton v-if="loading" :rows="1" animated />
                <span v-else class="text-warning">{{ pendingShip }}</span>
              </div>
              <div class="stat-label">待发货</div>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- 商品总数 -->
      <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-content">
            <div class="stat-icon product-icon">
              <Icon icon="ep:goods" :size="28" />
            </div>
            <div class="stat-info">
              <div class="stat-value">
                <el-skeleton v-if="loading" :rows="1" animated />
                <span v-else>{{ totalProducts }}</span>
              </div>
              <div class="stat-label">商品总数 (在售{{ onSaleProducts }})</div>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- 钱包余额 -->
      <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-content">
            <div class="stat-icon wallet-icon">
              <Icon icon="ep:wallet" :size="28" />
            </div>
            <div class="stat-info">
              <div class="stat-value">
                <el-skeleton v-if="loading" :rows="1" animated />
                <span v-else>¥{{ walletBalance }}</span>
              </div>
              <div class="stat-label">可提现余额</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 快捷入口 + 店铺信息 -->
    <el-row :gutter="16">
      <!-- 快捷入口 -->
      <el-col :xs="24" :lg="14" class="mb-16px">
        <ContentWrap title="快捷入口">
          <div v-if="quickLinks.length" class="quick-links">
            <div
              v-for="item in quickLinks"
              :key="item.path"
              class="quick-link-item"
              @click="goTo(item.path)"
            >
              <div class="link-icon" :class="item.iconClass">
                <Icon :icon="item.icon" :size="20" />
              </div>
              <span class="link-label">{{ item.label }}</span>
            </div>
          </div>
          <el-empty v-else description="暂无可用快捷入口" :image-size="64" />
        </ContentWrap>
      </el-col>

      <!-- 店铺信息 -->
      <el-col :xs="24" :lg="10" class="mb-16px">
        <ContentWrap title="店铺信息">
          <div class="shop-info">
            <div class="info-row" v-if="shopInfo?.shopName">
              <span class="info-label">店铺名称</span>
              <span class="info-value">{{ shopInfo.shopName }}</span>
            </div>
            <div class="info-row" v-if="shopInfo?.shopDesc">
              <span class="info-label">店铺简介</span>
              <span class="info-value">{{ shopInfo.shopDesc }}</span>
            </div>
            <div class="info-row" v-if="shopInfo?.contactPhone">
              <span class="info-label">联系电话</span>
              <span class="info-value">{{ shopInfo.contactPhone }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">创建时间</span>
              <span class="info-value">{{ shopInfo?.createTime ? formatDate(shopInfo.createTime) : '-' }}</span>
            </div>
          </div>
        </ContentWrap>
      </el-col>
    </el-row>

    <!-- 经营图表 -->
    <el-row :gutter="16">
      <el-col :xs="24" :lg="14" class="mb-16px">
        <ContentWrap title="近 7 日订单趋势">
          <el-skeleton v-if="chartLoading" :rows="6" animated />
          <Echart v-else :height="320" :options="trendChartOptions" />
        </ContentWrap>
      </el-col>
      <el-col :xs="24" :lg="10" class="mb-16px">
        <ContentWrap title="订单状态分布">
          <el-skeleton v-if="chartLoading" :rows="6" animated />
          <Echart v-else :height="320" :options="statusChartOptions" />
        </ContentWrap>
      </el-col>
    </el-row>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, reactive } from 'vue'
import { useRouter } from 'vue-router'
import type { EChartsOption } from 'echarts'
import {
  getShopInfo,
  getTodayOrderCount,
  getPendingShipCount,
  getProductCount,
  getOnSaleProductCount,
  getOrderTrendLast7Days,
  getOrderStatusDistribution,
  getWalletSoft
} from '@/api/merchant/dashboard'
import { ContentWrap } from '@/components/ContentWrap'
import Echart from '@/components/Echart/src/Echart.vue'
import { formatDate } from '@/utils/formatTime'
import { storePath } from '@/views/merchant/registry/paths'
import { isMenuItemAllowed } from '@/views/merchant/registry/modules'
import { getMerchantSession } from '@/views/merchant/registry/merchant-session'
import type { ConsoleMenuItem } from '@/views/merchant/registry/consoles'

const router = useRouter()
const loading = ref(true)
const chartLoading = ref(true)

/** 与侧栏 consoles.ts 权限对齐的快捷入口 */
const QUICK_LINK_DEFS: {
  path: string
  label: string
  icon: string
  iconClass: string
  permission?: string
  anyPermissions?: string[]
}[] = [
  {
    path: '/product/form',
    label: '发布商品',
    icon: 'ep:plus',
    iconClass: 'product-link-icon',
    permission: 'shop:product:create'
  },
  {
    path: '/product',
    label: '商品管理',
    icon: 'ep:goods',
    iconClass: 'product-manage-link-icon',
    permission: 'shop:product:query'
  },
  {
    path: '/order',
    label: '订单管理',
    icon: 'ep:list',
    iconClass: 'order-link-icon',
    permission: 'merchant:order:query'
  },
  {
    path: '/finance',
    label: '财务管理',
    icon: 'ep:money',
    iconClass: 'finance-link-icon',
    anyPermissions: ['shop:finance:wallet', 'shop:finance:bill', 'shop:finance:withdraw']
  },
  {
    path: '/promotion',
    label: '营销管理',
    icon: 'ep:present',
    iconClass: 'promo-link-icon',
    anyPermissions: [
      'shop:promotion:coupon-template:query',
      'shop:promotion:coupon:query',
      'shop:promotion:discount-activity:query',
      'shop:promotion:seckill-activity:query',
      'shop:promotion:reward-activity:query',
      'shop:promotion:combination-activity:query',
      'shop:promotion:bargain-activity:query'
    ]
  },
  {
    path: '/shop',
    label: '店铺管理',
    icon: 'ep:shop',
    iconClass: 'shop-link-icon',
    permission: 'shop:info:query'
  }
]

const quickLinks = computed(() => {
  const perms = new Set(getMerchantSession()?.permissions || [])
  return QUICK_LINK_DEFS.filter((item) =>
    isMenuItemAllowed(item as ConsoleMenuItem, perms)
  )
})

const shopInfo = ref<any>(null);
const todayOrders = ref(0);
const pendingShip = ref(0);
const totalProducts = ref(0);
const onSaleProducts = ref(0);
const walletBalance = ref('0.00');

const greeting = computed(() => {
  const h = new Date().getHours();
  if (h < 6) return '夜深了';
  if (h < 12) return '早上好';
  if (h < 14) return '中午好';
  if (h < 18) return '下午好';
  return '晚上好';
});

const trendChartOptions = reactive<EChartsOption>({
  tooltip: { trigger: 'axis' },
  grid: { left: 40, right: 20, top: 30, bottom: 30 },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: [] as string[],
    axisTick: { show: false }
  },
  yAxis: {
    type: 'value',
    minInterval: 1,
    splitLine: { lineStyle: { type: 'dashed' } }
  },
  series: [
    {
      name: '订单数',
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 8,
      data: [] as number[],
      itemStyle: { color: '#409eff' },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(64,158,255,0.28)' },
            { offset: 1, color: 'rgba(64,158,255,0.02)' }
          ]
        }
      }
    }
  ]
})

const statusChartOptions = reactive<EChartsOption>({
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  legend: { bottom: 0, icon: 'circle' },
  series: [
    {
      name: '订单状态',
      type: 'pie',
      radius: ['42%', '68%'],
      center: ['50%', '46%'],
      avoidLabelOverlap: true,
      itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
      label: { formatter: '{b}\n{c}' },
      data: [] as { name: string; value: number }[]
    }
  ]
})

async function fetchDashboardData() {
  loading.value = true;
  chartLoading.value = true;
  try {
    const [
      shopRes,
      walletRes,
      todayOrderRes,
      pendingShipRes,
      productRes,
      onSaleRes,
    ] = await Promise.all([
      getShopInfo(),
      getWalletSoft(),
      getTodayOrderCount(),
      getPendingShipCount(),
      getProductCount(),
      getOnSaleProductCount(),
    ]);

    shopInfo.value = shopRes || {};

    const wallet = walletRes || {};
    // 可提现 = usableAmount（元）；勿用 totalAmount（累计已结算），后端也不是分
    const usable = Number(wallet.usableAmount ?? 0);
    walletBalance.value = (Number.isFinite(usable) ? usable : 0).toFixed(2);

    todayOrders.value = todayOrderRes?.total || 0;
    pendingShip.value = pendingShipRes?.total || 0;
    totalProducts.value = productRes?.total || 0;
    onSaleProducts.value = onSaleRes?.total || 0;
  } catch (e) {
    console.warn('Dashboard data fetch error:', e);
  } finally {
    loading.value = false;
  }

  try {
    const [trend, statusDist] = await Promise.all([
      getOrderTrendLast7Days(),
      getOrderStatusDistribution(),
    ])
    trendChartOptions.xAxis!.data = trend.dates
    ;(trendChartOptions.series as any[])[0].data = trend.orderCount
    ;(statusChartOptions.series as any[])[0].data = statusDist.filter((i) => i.value > 0)
    if (!(statusChartOptions.series as any[])[0].data.length) {
      ;(statusChartOptions.series as any[])[0].data = [{ name: '暂无订单', value: 0 }]
    }
  } catch (e) {
    console.warn('Dashboard chart fetch error:', e)
  } finally {
    chartLoading.value = false
  }
}

function goTo(path: string) {
  router.push(storePath(path))
}

onMounted(() => {
  fetchDashboardData();
});
</script>

<style lang="scss" scoped>
.dashboard-container {
  padding: 0;
}

// 欢迎区域
.welcome-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.welcome-title {
  font-size: 22px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  margin: 0 0 6px 0;
}

.welcome-subtitle {
  font-size: 14px;
  color: var(--el-text-color-secondary);
  margin: 0;
}

// 统计卡片
.stat-card {
  border-radius: 10px;
  cursor: default;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.order-icon {
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  color: #1976d2;
}
.ship-icon {
  background: linear-gradient(135deg, #fff3e0, #ffe0b2);
  color: #f57c00;
}
.product-icon {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  color: #388e3c;
}
.wallet-icon {
  background: linear-gradient(135deg, #fce4ec, #f8bbd0);
  color: #c2185b;
}

.stat-info {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--el-text-color-primary);
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: var(--el-text-color-secondary);
  margin-top: 4px;
}

.text-warning {
  color: #f57c00;
}

// 快捷入口
.quick-links {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 16px;
}

.quick-link-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 12px;
  border-radius: 10px;
  background: var(--el-fill-color-light);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--el-color-primary-light-9);
    transform: translateY(-2px);
  }
}

.link-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.product-link-icon {
  background: #e3f2fd;
  color: #1976d2;
}
.product-manage-link-icon {
  background: #e8f5e9;
  color: #388e3c;
}
.order-link-icon {
  background: #f3e5f5;
  color: #7b1fa2;
}
.finance-link-icon {
  background: #e8f5e9;
  color: #388e3c;
}
.promo-link-icon {
  background: #fff3e0;
  color: #f57c00;
}
.shop-link-icon {
  background: #eee;
  color: #616161;
}

.link-label {
  font-size: 13px;
  color: var(--el-text-color-regular);
}

// 店铺信息
.shop-info {
  .info-row {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid var(--el-border-color-lighter);

    &:last-child {
      border-bottom: none;
    }
  }

  .info-label {
    width: 80px;
    flex-shrink: 0;
    font-size: 14px;
    color: var(--el-text-color-secondary);
  }

  .info-value {
    font-size: 14px;
    color: var(--el-text-color-primary);
  }
}

.mb-16px {
  margin-bottom: 16px;
}
</style>
